import React, { useEffect, useMemo, useState } from 'react'
import { Input, Modal, Select, Switch } from 'antd'
import { useTranslation } from 'react-i18next'
import { CloseOutlined } from '@ant-design/icons'
import { Rule } from 'antd/es/form'

import { useGetTelePhonePrefixes } from '@hooks'
import { useUser } from '@contexts'

import { StyledForm, StyledRow } from '../../../../../components/StyledComponents'
import { countryCodeByCompanyId, defaultTelephonePreffix } from '../../../../../constants'
import { useUpdateCarrier } from '../../../../../hooks/useUpdateCarrier'
import { Carrier } from '../../../../../types'

const formValidations = {
  businessName: {
    required: 'globals.obligatory_field',
    format: 'carrier_modal.businessName_format_require',
    min: 1,
    max: 50,
  },
  tradeName: {
    required: 'globals.obligatory_field',
    format: 'carrier_modal.tradeName_format_require',
    min: 1,
    max: 50,
  },
  email: { required: 'globals.obligatory_field', format: 'carrier_modal.email_format' },
  cellphone: {
    required: 'globals.obligatory_field',
    format: 'carrier_modal.cellphone_format_require',
    min: 0,
    max: 12,
  },
  phone: { min: 0, max: 15 },
  sapId: {
    required: 'globals.obligatory_field',
    format: 'carrier_modal.sap_code_format_require',
    min: 6,
    max: 6,
  },
  password: {
    required: 'globals.obligatory_field',
    format: 'carrier_modal.password_format_require',
    min: 8,
    regex: /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[@$!%*?&._])[A-Za-z0-9@$!%*?&._]{8,}$/,
  },
}

const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

type Props = {
  isOpen: boolean
  onClose: () => void
  selectedCarrier: number
  carriersData: Carrier[]
}

const EditCarrierModal: React.FC<Props> = ({ isOpen, onClose, selectedCarrier, carriersData }) => {
  const [selectedPreffix, setSelectedPrefix] = useState<number>()
  const { t } = useTranslation()
  const { telephonePrefixes } = useGetTelePhonePrefixes()
  const { state: user } = useUser()

  const country = useMemo(() => {
    return user?.company?.id
      ? countryCodeByCompanyId[user.company.id as keyof typeof countryCodeByCompanyId]
      : undefined
  }, [user])

  const [form] = StyledForm.useForm()
  const { resetFields } = form

  const { updateCarrier } = useUpdateCarrier(onClose)

  const carrier = useMemo(
    () => carriersData.find(({ id }) => id === selectedCarrier) ?? undefined,
    [carriersData, selectedCarrier],
  )

  const [disableAtSwitch, setDisableAtSwitch] = useState(false)

  useEffect(() => {
    if (isOpen && carrier) {
      setDisableAtSwitch(carrier.disableAt === null)
    }
  }, [isOpen, carrier, selectedCarrier])

  const validateEmail = (_rule: Rule, value: string) => {
    if (!value) {
      return Promise.reject(t(formValidations.email.required))
    }
    if (!regex.test(value)) {
      return Promise.reject(t(formValidations.email.format))
    }
    return Promise.resolve()
  }

  const onOk = () => {
    form
      .validateFields()
      .then(values => {
        const { sapId, phone, address, ...dataToSend } = values
        const formattedPhone = phone === '' ? null : phone
        const formattedAddress = address === '' ? null : address
        updateCarrier(selectedCarrier, {
          ...dataToSend,
          phone: formattedPhone,
          address: formattedAddress,
          disabled: !disableAtSwitch,
        })
      })
      .catch(console.error)
  }

  const initialValues = useMemo(() => {
    return {
      ...carrier,
      country,
    }
  }, [carrier, country])

  useEffect(() => {
    if (!selectedPreffix && carrier) {
      if (carrier.prefix) {
        setSelectedPrefix(carrier.prefix.id)
      } else {
        const defaultPreffix = telephonePrefixes.find(
          ({ prefix }) => prefix === defaultTelephonePreffix,
        )
        setSelectedPrefix(defaultPreffix ? defaultPreffix.id : telephonePrefixes[0]?.id)
      }
    }
  }, [telephonePrefixes, carrier, selectedPreffix])

  useEffect(() => {
    if (isOpen && carrier) {
      form.setFieldsValue(initialValues)
    }
  }, [isOpen, carrier, form, initialValues])

  useEffect(() => {
    if (!isOpen) {
      resetFields()
    }
  }, [isOpen, resetFields])

  return (
    <Modal
      open={isOpen}
      keyboard
      maskClosable
      onCancel={() => {
        resetFields()
        onClose()
      }}
      title={t('carrier_modal.edit_carrier')}
      closeIcon={<CloseOutlined />}
      okText={t('carrier_modal.button_add')}
      cancelText={t('carrier_modal.button_cancel')}
      onOk={onOk}
      destroyOnClose
    >
      <StyledForm form={form} initialValues={initialValues}>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            name="businessName"
            label={t('carrier_modal.Business_name')}
            rules={[
              { required: true, message: t(formValidations.businessName.required) },
              {
                min: formValidations.businessName.min,
                message: t(formValidations.businessName.format, {
                  min: formValidations.businessName.min,
                }),
              },
              {
                max: formValidations.businessName.max,
                message: t(formValidations.businessName.format, {
                  max: formValidations.businessName.max,
                }),
              },
            ]}
            hasFeedback
          >
            <Input />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            name="tradeName"
            label={t('carrier_modal.trade_name')}
            rules={[
              { required: true, message: t(formValidations.tradeName.required) },
              {
                min: formValidations.tradeName.min,
                message: t(formValidations.tradeName.format, {
                  min: formValidations.tradeName.min,
                }),
              },
              {
                max: formValidations.tradeName.max,
                message: t(formValidations.tradeName.format, {
                  max: formValidations.tradeName.max,
                }),
              },
            ]}
            hasFeedback
          >
            <Input />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item name="country" label={t('carrier_modal.country')}>
            <Input disabled />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            name="email"
            label={t('carrier_modal.email')}
            rules={[{ validator: validateEmail }]}
            required
            hasFeedback
          >
            <Input />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            name="phone"
            label={t('carrier_modal.phone')}
            rules={[
              {
                min: formValidations.phone.min,
                message: t('carrier_modal.min_length_error', {
                  min: formValidations.phone.min,
                }),
              },
              {
                max: formValidations.phone.max,
                message: t('carrier_modal.max_length_error', {
                  max: formValidations.phone.max,
                }),
              },
            ]}
            hasFeedback
          >
            <Input />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            name="cellphone"
            label={t('carrier_modal.cellphone')}
            rules={[
              { required: true, message: t(formValidations.cellphone.required) },
              {
                min: formValidations.cellphone.min,
                message: t('carrier_modal.min_length_error', {
                  min: formValidations.cellphone.min,
                }),
              },
              {
                max: formValidations.cellphone.max,
                message: t('carrier_modal.max_length_error', {
                  max: formValidations.cellphone.max,
                }),
              },
            ]}
            hasFeedback
          >
            <Input
              addonBefore={
                <Select
                  defaultValue="54"
                  style={{ width: 80 }}
                  onChange={value => {
                    setSelectedPrefix(Number(value))
                  }}
                >
                  {telephonePrefixes?.map((prefix: { prefix: string }) => (
                    <Select.Option key={prefix.prefix} value={prefix.prefix}>
                      {`+${prefix.prefix}`}
                    </Select.Option>
                  ))}
                </Select>
              }
              onChange={e => {
                const enteredValue = e.target.value
                form.setFieldValue('cellphone', enteredValue)
              }}
            />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item name="address" label={t('carrier_modal.address')}>
            <Input />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item name="sapId" label={t('carrier_modal.Sap_code')} hasFeedback>
            <Input type="number" disabled />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item label="Estado" name="disableAt">
            <Switch
              checked={disableAtSwitch}
              onChange={checked => {
                setDisableAtSwitch(checked)
              }}
              checkedChildren="HABILITADO"
              unCheckedChildren="DESHABILITADO"
            />
          </StyledForm.Item>
        </StyledRow>
      </StyledForm>
    </Modal>
  )
}

export { EditCarrierModal }
